import { type ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSync } from '../../hooks/useSync';
import { useAdminStatus } from '../../hooks/useAdminStatus';
import { useSpacesStore } from '../../features/spaces/spacesStore';
import { ThemeToggle } from '../common/ThemeToggle';
import { FeedbackButton } from '../feedback/FeedbackButton';
import { CollapsibleSection } from './CollapsibleSection';
import { SpaceNavItem } from './SpaceNavItem';
import { CreateSpaceButton } from './CreateSpaceButton';
import { PendingInvitationsBanner } from '../invitations/PendingInvitationsBanner';

interface DashboardLayoutProps {
  children: ReactNode;
}

export const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const { user, signOut } = useAuth();
  const { getPersonalSpaces, getSharedSpaces, loadSpaces } = useSpacesStore();
  const { isAdmin, isLoading: isAdminLoading } = useAdminStatus();
  const location = useLocation();
  const navigate = useNavigate();
  // Start with sidebar closed on mobile (< 1024px), open on desktop
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);

  // Initialize sync system for all protected pages
  const { isSyncing, lastSync, error: syncError} = useSync();

  // Load spaces on mount to ensure they're available after refresh
  useEffect(() => {
    if (user) {
      loadSpaces();
    }
    // loadSpaces is a Zustand action and is stable, don't include in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Get spaces filtered by personal vs shared
  const personalSpaces = user ? getPersonalSpaces(user.uid) : [];
  const sharedSpaces = user ? getSharedSpaces(user.uid) : [];

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => {
    // For /notes, check if it's the base notes page (no spaceId param)
    // When spaceId is present, the shared space nav item should be highlighted instead
    if (path === '/notes') {
      const searchParams = new URLSearchParams(location.search);
      return location.pathname === path && !searchParams.get('spaceId');
    }
    return location.pathname === path;
  };

  return (
    <div className="h-screen overflow-hidden bg-soft-cream dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 shadow-sm flex-shrink-0">
        <div className="flex items-center justify-between h-16 px-2 sm:px-4">
          {/* Left: Logo & Menu Toggle */}
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-nest-brown to-warm-orange rounded-lg flex items-center justify-center shadow">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                  />
                </svg>
              </div>
              <span className="text-lg sm:text-xl font-bold text-nest-brown dark:text-warm-orange hidden sm:block">NIDOnote</span>
            </Link>
          </div>

          {/* Center: Theme Toggle */}
          <div className="flex-1 flex justify-center max-w-[100px] sm:max-w-none">
            <ThemeToggle />
          </div>

          {/* Right: User Menu */}
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-1 sm:gap-2 p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="User menu"
            >
              <div className="w-8 h-8 bg-sky-blue dark:bg-sky-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              <span className="hidden md:block text-sm font-medium text-gray-700 dark:text-gray-300">
                {user?.email?.split('@')[0] || 'User'}
              </span>
              <svg
                className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform hidden sm:block ${isUserMenuOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* User Dropdown Menu */}
            {isUserMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsUserMenuOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.email}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Free Plan</p>
                  </div>
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate('/settings');
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Settings
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event from bubbling to backdrop
                      setIsUserMenuOpen(false);
                      handleSignOut();
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 border-t border-gray-200 dark:border-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Pending Invitations Banner */}
      <PendingInvitationsBanner />

      <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
        {/* Sidebar */}
        <aside
          className={`${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } fixed inset-y-0 left-0 z-30 w-full sm:w-80 lg:w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative mt-16 lg:mt-0 flex flex-col`}
        >
          {/* Scrollable Navigation */}
          <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
            {/* Dashboard Link */}
            <Link
              to="/dashboard"
              onClick={() => setIsSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                isActive('/dashboard')
                  ? 'bg-sky-blue dark:bg-sky-blue-600 text-white shadow-sm'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              Dashboard
            </Link>

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

            {/* Personal Spaces Section */}
            <CollapsibleSection
              title="Personal"
              storageKey="sidebar_personal"
              defaultExpanded={true}
            >
              {/* Default Personal Items - Calendar and Notes */}
              <Link
                to="/calendar"
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive('/calendar')
                    ? 'bg-sky-blue dark:bg-sky-blue-600 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="text-lg">📅</span>
                <div className="flex-1">
                  <div className="font-medium">Calendar</div>
                  <div className="text-xs opacity-75">Events & Schedule</div>
                </div>
              </Link>

              <Link
                to="/notes"
                onClick={() => setIsSidebarOpen(false)}
                className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive('/notes')
                    ? 'bg-sky-blue dark:bg-sky-blue-600 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <span className="text-lg">📝</span>
                <div className="flex-1">
                  <div className="font-medium">Notes</div>
                  <div className="text-xs opacity-75">Quick Notes</div>
                </div>
              </Link>

              {/* User's Personal Spaces */}
              {personalSpaces.map((space) => (
                <SpaceNavItem key={space.id} space={space} />
              ))}

              <CreateSpaceButton type="personal" />
            </CollapsibleSection>

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

            {/* Shared Spaces Section */}
            <CollapsibleSection
              title="Shared"
              storageKey="sidebar_shared"
              defaultExpanded={true}
            >
              {sharedSpaces.length > 0 ? (
                sharedSpaces.map((space) => (
                  <SpaceNavItem key={space.id} space={space} />
                ))
              ) : (
                <div className="px-3 py-4 text-xs text-gray-500 dark:text-gray-400 text-center">
                  No shared spaces yet
                </div>
              )}
            </CollapsibleSection>

            {/* Admin Section - Only visible to admins */}
            {!isAdminLoading && isAdmin && (
              <>
                {/* Divider */}
                <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

                <div className="pt-4 mt-4">
                  <button
                    onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                    className="flex items-center justify-between w-full px-4 py-3 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Admin
                    </div>
                    <svg
                      className={`w-4 h-4 transition-transform ${isAdminMenuOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Admin Submenu */}
                  {isAdminMenuOpen && (
                    <div className="mt-2 space-y-1 ml-4">
                      <Link
                        to="/admin/alpha-testers"
                        onClick={() => setIsSidebarOpen(false)}
                        className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          isActive('/admin/alpha-testers')
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        Alpha Testers
                      </Link>
                      <Link
                        to="/admin/feedback"
                        onClick={() => setIsSidebarOpen(false)}
                        className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          isActive('/admin/feedback')
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                        </svg>
                        Feedback Dashboard
                      </Link>
                      <Link
                        to="/admin/manage-admins"
                        onClick={() => setIsSidebarOpen(false)}
                        className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          isActive('/admin/manage-admins')
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        Manage Admins
                      </Link>
                    </div>
                  )}
                </div>
              </>
            )}
          </nav>

          {/* Sidebar Footer - Fixed at bottom */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <span>v0.0.1-alpha</span>
              <span>•</span>
              <div className="flex items-center gap-1 min-w-[70px]">
                {isSyncing ? (
                  <>
                    <div className="w-2 h-2 bg-sky-blue rounded-full animate-pulse" />
                    <span>Syncing...</span>
                  </>
                ) : syncError ? (
                  <>
                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                    <span className="text-red-600">Error</span>
                  </>
                ) : lastSync ? (
                  <>
                    <div className="w-2 h-2 bg-fresh-green rounded-full" />
                    <span>Synced</span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-gray-400 rounded-full" />
                    <span>Pending</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </aside>

        {/* Sidebar Overlay (Mobile) */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-2 sm:p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>

      {/* Feedback Button (Floating) */}
      <FeedbackButton />
    </div>
  );
};
